import React from 'react'
import { Company, COMPANIES_SEARCH_MAX_PAGE_SIZE } from 'models'
import { useGetParentCompanies } from 'utils/hooks/queries/useGetParentCompanies'
import { useGetOrphanCompanies } from 'utils/hooks/queries/useGetOrphanCompanies'
import { useGetChildrenCompanies } from 'utils/hooks/queries/useGetChildrenCompanies'
import { useGetCompanyById } from 'utils/hooks/queries/useGetCompanyById'
import { sortEntities } from 'utils/helpers/sortEntities'
import { useAuthenticatedUser } from '../../UserContext'
import { companiesActions, type CompaniesReducerAction } from '../reducer'
import { resolveB2CContext } from '../helpers/resolveB2CContext'

type UseCompanyQueriesOptions = {
  parentSearchValue: string
  childrenSearchValue: string
  selectedParent: Company | null
  isOrphanSelected: boolean
}

const useCompanyQueries = (
  dispatch: React.Dispatch<CompaniesReducerAction>,
  options: UseCompanyQueriesOptions,
) => {
  const { selectedParent, isOrphanSelected } = options
  const {
    authenticatedUser,
    contact,
    isLoading: isAuthenticatedUserLoading,
    isLoginB2C,
  } = useAuthenticatedUser()
  const parentSearch = options.parentSearchValue.trim() || undefined
  const childrenSearch = options.childrenSearchValue.trim() || undefined

  const assignedEntityId = (contact.assignedEntityId || '').trim()
  const isAuthenticated = Boolean(authenticatedUser.clientPrincipal)

  const parentQuery = useGetParentCompanies(parentSearch, {
    enabled: !isAuthenticatedUserLoading && isAuthenticated && !isLoginB2C,
  })
  const orphanQuery = useGetOrphanCompanies(childrenSearch, {
    enabled: options.isOrphanSelected && isAuthenticated && !isLoginB2C,
  })

  React.useEffect(() => {
    if (parentQuery.isSuccess && parentQuery.data) {
      const sortedParentCompanies = sortEntities([...parentQuery.data])
      dispatch({ type: companiesActions.SET_PARENT_COMPANIES, companies: sortedParentCompanies })
    }
  }, [dispatch, parentQuery.data, parentQuery.isSuccess])

  const resolvedChildrenCount = React.useMemo(() => {
    if (!selectedParent) return undefined
    if (typeof selectedParent.childrenCount === 'number') {
      return selectedParent.childrenCount
    }
    if (Array.isArray(selectedParent.children)) {
      return selectedParent.children.length
    }
    return undefined
  }, [selectedParent])

  const shouldFetchLargeFamilyRemotely =
    !isOrphanSelected &&
    typeof resolvedChildrenCount === 'number' &&
    resolvedChildrenCount > COMPANIES_SEARCH_MAX_PAGE_SIZE &&
    Boolean(childrenSearch)
  const isUsingRemoteChildren = isOrphanSelected || shouldFetchLargeFamilyRemotely

  const childrenQuery = useGetChildrenCompanies(isOrphanSelected ? null : selectedParent, {
    search: shouldFetchLargeFamilyRemotely ? childrenSearch : undefined,
  })

  const fallbackChildrenCount = React.useMemo(() => {
    if (isUsingRemoteChildren || !childrenQuery.data) {
      return undefined
    }
    return childrenQuery.data.length
  }, [childrenQuery.data, isUsingRemoteChildren])

  const parentChildrenCount = resolvedChildrenCount ?? fallbackChildrenCount ?? 0

  const assignedCompanyQuery = useGetCompanyById(assignedEntityId, {
    enabled: isLoginB2C && Boolean(assignedEntityId),
  })
  const assignedCompany = assignedCompanyQuery.data ?? null
  const assignedParentId = assignedCompany?.parentId?.trim() || ''

  const assignedParentQuery = useGetCompanyById(assignedParentId, {
    enabled: isLoginB2C && Boolean(assignedParentId),
  })

  const b2cContext = React.useMemo(
    () =>
      resolveB2CContext({
        isLoginB2C,
        assignedCompany,
        assignedParentCompany: assignedParentQuery.data ?? null,
      }),
    [assignedCompany, assignedParentQuery.data, isLoginB2C],
  )

  React.useEffect(() => {
    if (orphanQuery.isSuccess && orphanQuery.data) {
      const sortedOrphanCompanies = sortEntities([...orphanQuery.data])
      dispatch({ type: companiesActions.SET_ORPHAN_COMPANIES, companies: sortedOrphanCompanies })
    }
  }, [dispatch, orphanQuery.data, orphanQuery.isSuccess])

  const isB2CBootstrapLoading = React.useMemo(
    () =>
      Boolean(
        isLoginB2C &&
          (assignedCompanyQuery.isLoading ||
            (Boolean(assignedParentId) && assignedParentQuery.isLoading)),
      ),
    [assignedCompanyQuery.isLoading, assignedParentId, assignedParentQuery.isLoading, isLoginB2C],
  )

  return {
    parentQuery,
    orphanQuery,
    childrenQuery,
    parentChildrenCount,
    isUsingRemoteChildren,
    userCompany: b2cContext.userCompany,
    b2cAutoSelectedChildId: b2cContext.b2cAutoSelectedChildId,
    b2cAutoSelectedChild: b2cContext.b2cAutoSelectedChild,
    isB2CNoFamily: b2cContext.isB2CNoFamily,
    isB2CBootstrapLoading,
    isLoginB2C,
  }
}

export { useCompanyQueries }
